<template>
  <div>
    <b-overlay
      :show="!dataIsReady"
      spinner-type="border"
      variant="light"
      spinner-variant="primary"
      opacity="1"
      rounded>
      <failed-data-get
        v-if="failedGetResellerStatus || failedIntegrationGetStatus"
        text="Looks like this reseller doesn't exist." />

      <template v-else>
        <div class="d-flex align-items-end pt-2 pb-6">
          <div class="d-flex flex-column">
            <h1 class="h5 mb-2">
              RevenueCat Integration
            </h1>
            <b-breadcrumb class="breadcrumb-transparent breadcrumb-line p-0 m-0">
              <b-breadcrumb-item :to="{ name: 'Dashboard' }">
                <i class="fa fa-home fa-sm mr-2"></i>
                Home
              </b-breadcrumb-item>
              <b-breadcrumb-item :to="{ name: 'ResellersList' }">Resellers</b-breadcrumb-item>
              <b-breadcrumb-item :to="{ name: 'ResellerIntegrations', params: { id: routeId } }">Intergations</b-breadcrumb-item>
              <b-breadcrumb-item active>RevenueCat</b-breadcrumb-item>
            </b-breadcrumb>
          </div>
        </div>

        <b-row>
          <b-col
            xl="9"
            class="pr-4 pr-xl-16">
            <b-card class="card-custom card-border mb-4 mb-lg-8">
              <b-row>
                <b-col
                  lg="10"
                  xl="9">
                  <!-- Wrapper for fake element + checkbox -->
                  <div
                    class="position-relative mb-5"
                    style="width: fit-content;">
                    <!-- Fake element above checkbox that TRIGGERS event -->
                    <div
                      class="w-100 h-100 position-absolute"
                      style="z-index: 20;"
                      role="button"
                      @click="handleRevenuecatStatus" />
                    <!-- Checkbox that REPRESENTS current status, not changes it -->
                    <b-form-checkbox
                      :checked="revenuecatIsEnabled"
                      size="lg"
                      switch
                      class="switch-primary">
                      <span class="font-size-lg">Enable RevenueCat</span>
                    </b-form-checkbox>
                  </div>

                  <b-tabs
                    content-class="mt-8"
                    nav-class="nav-tabs-line">

                    <b-tab
                      title="API Keys"
                      active>
                      <!-- API key -->
                      <div class="mb-8">
                        <b-form
                          id="apiKey"
                          inline
                          class="w-100 mb-2 flex-wrap flex-sm-nowrap align-items-end">
                          <b-form-group class="flex-grow-1">
                            <template #label>
                              <div class="d-flex align-items-center">
                                <span>RevenueCat Secret API key (v1)</span>
                                <b-button
                                  v-b-popover.hover.html.right="apikeyTip"
                                  variant="link-info"
                                  class="ml-2 font-weight-normal">
                                  <i class="fa fa-question-circle fa-sm m-0"></i>
                                </b-button>
                              </div>
                            </template>
                            <b-form-input
                              v-model="revenuecatApikey"
                              :disabled="revenuecatResponses('setRevenuecatApikey')"
                              id="input-apiKey"
                              :readonly="!revenuecatIsEnabled"
                              class="flex-grow-1 w-100 mr-auto" />
                          </b-form-group>
                          <div class="d-flex">
                            <b-button
                              id="copyApiKey"
                              v-wave
                              variant="outline-primary"
                              data-clipboard-target="#input-apiKey"
                              class="ml-2"
                              :disabled="revenuecatResponses('setRevenuecatApikey')">
                              <i class="fa fa-copy fa-sm mr-1"></i>
                              <span>Copy</span>
                            </b-button>
                            <b-button
                              v-wave
                              variant="primary"
                              class="ml-2"
                              :class="{ 'no-events' : revenuecatResponses('setRevenuecatApikey') || !revenuecatIsEnabled }"
                              :disabled="revenuecatResponses('setRevenuecatApikey') || !revenuecatIsEnabled || revenuecatApikeyEmpty"
                              @click="setRevenuecatApikey">
                              <i class="fa fa-save fa-sm mr-1"></i>
                              <span>Save</span>
                            </b-button>
                          </div>
                        </b-form>
                      </div>
                      
                      <!-- Public key -->
                      <div>
                        <b-form
                          id="pubKey"
                          inline
                          class="w-100 mb-2 flex-wrap flex-sm-nowrap align-items-end">
                          <b-form-group class="flex-grow-1">
                            <template #label>
                              <div class="d-flex align-items-center">
                                <span>Public Key</span>
                                <b-button
                                  v-b-popover.hover.html.right="pubkeyTip"
                                  variant="link-info"
                                  class="ml-2 font-weight-normal">
                                  <i class="fa fa-question-circle fa-sm m-0"></i>
                                </b-button>
                              </div>
                            </template>
                            <b-form-input
                              v-model="revenuecatPubkey"
                              :disabled="revenuecatResponses('regeneratePubkey')"
                              id="pubKey-input"
                              readonly
                              class="flex-grow-1 w-100 mr-auto" />
                          </b-form-group>
                          <b-button
                            id="copyPubKey"
                            v-wave
                            variant="outline-primary"
                            data-clipboard-target="#pubKey-input"
                            class="ml-2"
                            :disabled="revenuecatResponses('regeneratePubkey')">
                            <i class="fa fa-copy fa-sm mr-1"></i>
                            <span>Copy</span>
                          </b-button>
                        </b-form>
                        <template v-if="confirmPubkeyRegen">
                          <b-alert
                            show
                            variant="light-danger"
                            class="mb-0">
                            <p class="mb-3">
                              Please ensure to update this new key across all your applications. Neglecting to do so, particularly for marketplace-hosted apps lacking remote key update capabilities, may lead to operational disruptions and potential service interruptions. Take immediate action to maintain seamless functionality and security.
                            </p>
                            <b-button
                              @click="closePubkeyRegenConfirm"
                              variant="outline-primary"
                              size="sm">
                              Cancel
                            </b-button>
                            <b-button
                              variant="danger"
                              size="sm"
                              class="ml-4 text-center justify-content-center flex-grow-1"
                              @click="regeneratePubkey(), closePubkeyRegenConfirm()">
                              <i class="fa fa-check fa-sm"></i>
                              <span>Yes, regenerate public key</span>
                            </b-button>
                          </b-alert>
                        </template>
                        <template v-else>
                          <b-button
                            variant="link-primary"
                            class="mr-2"
                            size="sm"
                            @click="confirmPubkeyRegenConfirm">
                            <i class="fa fa-redo-alt fa-sm"></i>
                            <span>Regenerate</span>
                          </b-button>
                        </template>
                      </div>
                    </b-tab>

                    <b-tab title="Entitlements">
                      <!-- Entitlements -->
                      <div class="mb-5 d-flex align-items-center">
                        <h6 class="mb-0 mt-0">Entitlements</h6>
                        <b-button
                          v-b-popover.hover.html.right="entitlementTip"
                          variant="link-info"
                          size="sm"
                          class="font-weight-normal ml-3">
                          <i class="fa fa-question-circle fa-sm m-0"></i>
                        </b-button>
                        <b-button
                          id="entitlements-settings"
                          variant="link-primary"
                          size="sm"
                          class="ml-auto">
                          <i class="fa fa-cog fa-sm"></i>
                          <span>Settings</span>
                        </b-button>
                        <b-popover
                          target="entitlements-settings"
                          :show.sync="showEntitlementsSettings"
                          triggers="click blur"
                          placement="bottom">
                          <b-form-group label="Default devices for new user">
                            <b-form-input
                              :value="1"
                              readonly />
                          </b-form-group>
                          <div class="d-flex align-items-center">
                            <b-button
                              variant="outline-secondary"
                              size="sm"
                              @click="showEntitlementsSettings = false">
                              <span>Cancel</span>
                            </b-button>
                            <b-button
                              variant="primary"
                              size="sm"
                              class="ml-auto"
                              @click="showEntitlementsSettings = false">
                              <span>Save</span>
                            </b-button>
                          </div>
                        </b-popover>
                      </div>

                      <b-table
                        :items="entitlementsList"
                        :fields="fields"
                        :class="{ 'table-hover' : !entitlementsListEmpty }"
                        stacked="md"
                        head-variant="light"
                        show-empty
                        responsive
                        class="mb-5">
                        <template #cell(order)="data">
                          <span class="small font-weight-light">{{ data.index + 1 }}</span>
                        </template>
                        <template #cell(shift)="data">
                          <b-button-group
                            v-if="!isNewEntitlementField(data.item._id)"
                            size="sm">
                            <b-button
                              variant="link-primary"
                              v-wave
                              :disabled="data.index == 0"
                              class="align-middle rounded"
                              @click="changeEntitlementOrder(data.item._id, 'up')">
                              <i class="fa fa-arrow-up fa-sm m-0 p-2 opacity-75"></i>
                            </b-button>
                            <b-button
                              variant="link-primary"
                              v-wave
                              :disabled="data.index + 1 === entitlementsListLength"
                              class="align-middle rounded"
                              @click="changeEntitlementOrder(data.item._id, 'down')">
                              <i class="fa fa-arrow-down fa-sm m-0 p-2 opacity-75"></i>
                            </b-button>
                          </b-button-group>
                        </template>
                        <template #cell(entitlement)="data">
                          <b-form-input
                            v-if="data.item.editable"
                            :id="`input-${data.item._id}-entitlement`"
                            size="sm"
                            placeholder="Default"
                            autocomplete="off"
                            :disabled="!isNewEntitlementField(data.item._id)"
                            :state="entitlementFieldInvalidState(data.item._id, 'entitlement')"
                            v-model="data.item.entitlement" />
                          <span
                            v-else
                            class="d-inline-block text-truncate line-height-xl">{{ data.item.entitlement }}</span>
                        </template>
                        <template #cell(devices)="data">
                          <b-form-input
                            v-if="data.item.editable"
                            :id="`input-${data.item._id}-devices`"
                            size="sm"
                            type="number"
                            min="1"
                            onkeypress="return event.charCode >= 48 && event.charCode <= 57"
                            onwheel="this.blur()"
                            placeholder="5"
                            :state="entitlementFieldInvalidState(data.item._id, 'devices')"
                            v-model.number="data.item.devices" />
                          <span
                            v-else
                            class="d-inline-block text-truncate  line-height-xl">{{ data.item.devices }}</span>
                        </template>
                        <template #cell(edit)="data">
                          <template v-if="data.item.editable">
                            <b-button
                              v-if="data.item._id"
                              size="sm"
                              variant="outline-primary"
                              class="mr-3 align-middle btn-icon"
                              @click="[
                                clearErrors(data.item._id),
                                switchEditableMode({
                                  entitlement: data.item,
                                  status: false
                                }),
                              ]">
                              <i class="fa fa-times fa-sm"></i>
                            </b-button>
                            <b-button
                              size="sm"
                              variant="outline-primary"
                              class="mr-4 align-middle"
                              :disabled="revenuecatResponses('addEntitlement') || revenuecatResponses('updateEntitlement')"
                              @click="[
                                clearErrors(data.item._id),
                                handleEntitlement(data.item)
                              ]">
                              <i class="fa fa-save fa-sm"></i>
                              <span>Save</span>
                            </b-button>
                          </template>
                          <template v-else>
                            <b-button
                              size="sm"
                              variant="outline-primary"
                              class="mr-4 align-middle"
                              @click="[
                                selectEntitlement(data.item),
                                switchEditableMode({
                                  entitlement: data.item,
                                  status: true
                                }),
                                focusInput(`${data.item._id}-devices`)
                              ]">
                              <i class="fa fa-pen fa-sm"></i>
                              <span>Edit</span>
                            </b-button>
                          </template>
                          <b-button
                            :id="`deleteButton-${data.item._id}`"
                            size="sm"
                            variant="outline-primary"
                            class="btn-icon"
                            :disabled="revenuecatResponses('deleteEntitlement')"
                            @click="[
                              selectEntitlement(data.item),
                              handleDelete(data.item._id),
                            ]">
                            <i class="fa fa-trash fa-sm"></i>
                          </b-button>
                        </template>
                        <template #empty="scope">
                          <no-data-found text="No entitlements yet" />
                          <b-button
                            size="sm"
                            v-wave
                            variant="transparent-primary"
                            class="align-middle mx-auto d-block"
                            @click="[
                              addNewEntitlementField(),
                              scrollToBottom(),
                              focusInput('null-entitlement')
                            ]">
                            <i class="fa fa-plus fa-sm p-0 pr-sm-2"></i>
                            <span class="d-none d-sm-inline">Add new entitlement</span>
                          </b-button>
                        </template>
                      </b-table>

                      <div
                        v-if="!entitlementsListEmpty"
                        class="d-flex align-items-center">
                        <b-button
                          size="sm"
                          v-wave
                          variant="outline-primary"
                          class="align-middle"
                          @click="[
                            addNewEntitlementField(),
                            scrollToBottom(),
                            focusInput('null-entitlement')
                          ]">
                          <i class="fa fa-plus fa-sm p-0 pr-sm-2"></i>
                          <span class="d-none d-sm-inline">Add new entitlement</span>
                        </b-button>
                        <b-button
                          variant="transparent-danger"
                          size="sm"
                          class="ml-auto"
                          @click="confirmClearEntitlements">
                          <i class="fa fa-trash fa-sm"></i>
                          <span>Clear all entitlements</span>
                        </b-button>
                      </div>
                      
                    </b-tab>
                  </b-tabs>

                </b-col>
              </b-row>
            </b-card>
          </b-col>
          <b-col xl="3">
            <b-card
              class="card-custom card-border"
              body-class="pb-lg-4">
              <reseller-details-plate :flush="true" />
            </b-card>
          </b-col>
        </b-row>
      </template>
    </b-overlay>

    <b-popover
      v-for="entitlement in entitlementsList"
      :key="`popover-${entitlement._id}`"
      :target="`deleteButton-${entitlement._id}`"
      :ref="`popover-${entitlement._id}`"
      triggers="manual blur"
      placement="left">
      <p>Are you sure you want to delete entitlement <span class="font-weight-bold">{{ selectedEntitlement.entitlement }}</span>?</p>
      <div class="d-flex w-100">
        <b-button
          @click="hideDeletePopover(entitlement._id)"
          variant="outline-primary"
          size="sm">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          class="ml-4 text-center justify-content-center flex-grow-1"
          @click="confirmDelete(entitlement._id), hideDeletePopover(entitlement._id)">
          <i class="fa fa-check fa-sm"></i>
          <span>Yes, delete</span>
        </b-button>
      </div>
    </b-popover>

    <modal-disable-revenuecat />
    <modal-clear-entitlements />
  </div>
</template>

<script>
import {
  // actions
  GET_RESELLER,
  
  // mutations
  SELECT_RESELLER,
  UNSELECT_RESELLER
} from '@/core/services/store/resellers.module'
import {
  // actions
  GET_REVENUECAT_DATA,
  SWITCH_REVENUECAT_STATUS,
  SET_REVENUECAT_APIKEY,
  REGENERATE_REVENUECAT_PUBKEY,
  GET_ENTITLEMENTS,
  SORT_ENTITLEMENTS,

  // mutations
  SAVE_REVENUECAT_STATUS,
  SAVE_REVENUECAT_API_KEY,
  SELECT_ENTITLEMENT,
  UNSELECT_ENTITLEMENT,
  ADD_ENTITLEMENT,
  UPDATE_ENTITLEMENT,
  DELETE_ENTITLEMENT,
  SWITCH_ENTITLEMENT_EDITABLE_STATE,
  ADD_NEW_ENTITLEMENT_FIELD,
  REMOVE_ENTITLEMENT,
  CLEAR_MANAGE_ENTITLEMENT_ERRORS,
  CLEAR_ENTITLEMENTS,
} from '@/core/services/store/reseller_revenuecat.module'
import { GET_INTEGRATIONS } from '@/core/services/store/integrations.module'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import {
  initClipboard,
  destroyClipboard,
  showToast,
  focusInput,
  scrollToBottom,
  showDeletePopover,
  hideDeletePopover
} from '@/core/utils'
import ModalDisableRevenuecat from './components/ModalDisableRevenuecat.vue'
import ModalClearEntitlements from './components/ModalClearEntitlements.vue'
import ResellerDetailsPlate from './components/ResellerDetailsPlate.vue'

export default {
  name: 'ResellerRevenuecat',
  components: {
    ModalDisableRevenuecat,
    ModalClearEntitlements,
    ResellerDetailsPlate,
  },
  data() {
    return {
      fields: [
        {
          label: '#',
          key: 'order',
          class: ['align-middle', 'pl-5'],
          thStyle: 'width: 50px;'
        },
        {
          label: '',
          key: 'shift',
          class: ['align-middle', 'px-0'],
          thStyle: 'width: 70px;'
        },
        {
          label: 'Entitlement',
          key: 'entitlement',
          class: ['align-middle', 'font-weight-bold', 'text-nowrap', 'pl-5'],
          thStyle: 'width: 40%;'
        },
        {
          label: 'Devices',
          key: 'devices',
          class: ['align-middle', 'text-nowrap'],
          thStyle: 'width: 15%;'
        },
        {
          label: 'Actions',
          key: 'edit',
          class: ['align-middle', 'text-md-right', 'text-nowrap', 'pr-5'],
        }
      ],

      dataIsReady: false,
      confirmPubkeyRegen: false,
      clipboard: null,
      showEntitlementsSettings: false,

      apikeyTip: 'Secret API keys is used to make any API request on behalf of your RevenueCat account, such as granting entitlement access and deleting subscribers for your. Check <a target="_blank" href="https://www.revenuecat.com/docs/api-v1/overview" style="text-decoration: underline;">official website</a> for more details.',

      pubkeyTip: 'This public key is used to verify the user from RevenueCat in the system at the client application level.',

      entitlementTip: 'RevenueCat entitlement offers flexible control over user access to app features. By default, access is granted on one device, but resellers can set rules for different subscription levels, allowing customized access based on subscriptions. More information on the <a target="_blank" href="https://www.revenuecat.com/docs/getting-started/entitlements" style="text-decoration: underline;">official website</a>.',

      initClipboard,
      destroyClipboard,
      showToast,
      focusInput,
      scrollToBottom,
      showDeletePopover,
      hideDeletePopover
    }
  },
  computed: {
    ...mapGetters([
      'integrationsList',
      'failedGetResellerStatus',
      'failedIntegrationGetStatus',
      'revenuecatApikey',
      'revenuecatApikeyEmpty',
      'revenuecatPubkey',
      'revenuecatPubkeyEmpty',
      'entitlementsList',
      'entitlementsListLength',
      'entitlementsListEmpty',
      'selectedEntitlement',
      'selectedEntitlementId',
      'noEmptyEntitlementFields',
      'isNewEntitlementField',
      'manageEntitlementErrors',
      'entitlementFieldInvalidState',
      'revenuecatResponses'
    ]),

    revenuecatIsEnabled: {
      get() {
        return this.$store.getters['revenuecatIsEnabled']
      },
      set(newVal) {
        this.$store.commit(SAVE_REVENUECAT_STATUS, newVal)
      }
    },

    revenuecatApikey: {
      get() {
        return this.$store.getters['revenuecatApikey']
      },
      set(newVal) {
        this.$store.commit(SAVE_REVENUECAT_API_KEY, newVal)
      }
    },

    routeId() {
      return this.$route.params.id
    },
  },
  watch: {
    /* eslint-disable-next-line no-unused-vars */
    routeId(newVal, oldVal) {
      if (newVal) {
        this.dataIsReady = false
        this.$store.commit(UNSELECT_RESELLER)
        this.getData(newVal)
      }
    },
  },
  created() {
    this.getData(this.routeId)

    eventBus.$on('showToast/ResellerRevenuecat', ({ message, variant }) => {
      this.showToast(message, variant)
    })
  },
  mounted() {
    this.initClipboard('copyApiKey')
    this.initClipboard('copyPubKey')
  },
  beforeDestroy() {
    this.dataIsReady = false
  },
  destroyed() {
    this.$store.commit(UNSELECT_RESELLER)

    this.destroyClipboard()
    eventBus.$off('showToast/ResellerRevenuecat')
  },
  methods: {
    getData(id) {
      let requirements = [
        () => this.$store.dispatch(GET_RESELLER, { id })
          .then(data => { this.selectReseller(data) }),
        () => this.$store.dispatch(GET_INTEGRATIONS, { id }),
        () => this.$store.dispatch(GET_REVENUECAT_DATA, { id }),
        () => this.$store.dispatch(GET_ENTITLEMENTS, { id })
      ]

      Promise
        .all(requirements.map(promiseFn => promiseFn()))
        .finally(() => this.dataIsReady = true)
    },

    selectReseller(reseller) {
      this.$store.commit(SELECT_RESELLER, reseller)
    },

    handleRevenuecatStatus() {
      if (!this.revenuecatIsEnabled) {
        this.$store
          .dispatch(SWITCH_REVENUECAT_STATUS, { id: this.routeId })
          .then(() => {
            this.focusInput('apiKey')
            this.$store.dispatch(GET_REVENUECAT_DATA, { id: this.routeId })
          })
      } else {
        eventBus.$emit('openModal', 'confirmRevenuecatDisable')
      }
    },

    setRevenuecatApikey() {
      this.$store.dispatch(SET_REVENUECAT_APIKEY, {
        id: this.routeId,
        apikey: this.revenuecatApikey
      })
    },

    confirmPubkeyRegenConfirm() {
      this.confirmPubkeyRegen = true
    },

    closePubkeyRegenConfirm() {
      this.confirmPubkeyRegen = false
    },

    regeneratePubkey() {
      this.$store.dispatch(REGENERATE_REVENUECAT_PUBKEY, { id: this.routeId })
    },

    selectEntitlement(entitlement) {
      this.$store.commit(SELECT_ENTITLEMENT, entitlement)
    },

    unselectEntitlement() {
      this.$store.commit(UNSELECT_ENTITLEMENT)
    },

    handleEntitlement(entitlement) {
      this.$store.dispatch(
        this.isNewEntitlementField(entitlement._id)
          ? ADD_ENTITLEMENT
          : UPDATE_ENTITLEMENT,
        {
          entitlement,
          id: this.routeId
        }
      )
    },

    handleDelete(id) {
      if (id) {
        this.showDeletePopover(id)
      } else {
        this.clearErrors(null)
        this.removeEntitlement(null)
      }
    },

    confirmDelete(id) {
      this.$store.dispatch(DELETE_ENTITLEMENT, {
        reseller_id: this.routeId,
        entitlement_id: id,
      })
    },

    switchEditableMode({ entitlement, status }) {
      this.$store.commit(SWITCH_ENTITLEMENT_EDITABLE_STATE, { entitlement, status })
    },

    addNewEntitlementField() {
      this.$store.commit(ADD_NEW_ENTITLEMENT_FIELD, {})
    },

    removeEntitlement(id) {
      this.$store.commit(REMOVE_ENTITLEMENT, id)
    },

    clearErrors(id) {
      this.$store.commit(CLEAR_MANAGE_ENTITLEMENT_ERRORS, id)
    },

    changeEntitlementOrder(id, direction) {
      // Compose an array with current items order
      const order = this.entitlementsList.map(element => element._id)

      // Get index of the passed ID in that array
      const index = order.indexOf(id)

      // Create a copy of the order array to avoid modifying the original data
      const modifiedOrder = [...order]

      // Modify the order in the copy
      if (direction === 'up' && index > 0) {
        // Move the item up by swapping it with the previous item
        const temp = modifiedOrder[index]
        modifiedOrder[index] = modifiedOrder[index - 1]
        modifiedOrder[index - 1] = temp
      }
      else if (direction === 'down' && index < modifiedOrder.length - 1) {
        // Move the item down by swapping it with the next item
        const temp = modifiedOrder[index]
        modifiedOrder[index] = modifiedOrder[index + 1]
        modifiedOrder[index + 1] = temp
      }

      // Send the modified order payload to the server
      this.sortEntitlements(modifiedOrder)
    },

    sortEntitlements(order) {
      this.$store
        .dispatch(SORT_ENTITLEMENTS, { id: this.routeId, order })
        .then(() => this.$store.dispatch(GET_ENTITLEMENTS, { id: this.routeId }))
    },

    confirmClearEntitlements() {
      if (
        !this.entitlementsListEmpty &&
        !this.isNewEntitlementField(this.entitlementsList[0]._id)) {
        eventBus.$emit('openModal', 'confirmEntitlementsClear')
      } else {
        this.$store.commit(CLEAR_ENTITLEMENTS)
      }
      
    },
  }
}
</script>
